import { GetTechAssessmentDto } from '../autogenerated/snrwbApiClient/models/GetTechAssessmentDto';
import { DefaultApi } from '../autogenerated/snrwbApiClient/apis/DefaultApi';
import {
  CreateUpdateTechAssessmentDto,
  CreateUpdateTechAssessmentDtoFromJSON,
  CreateUpdateOrganizationDto,
} from '../autogenerated/snrwbApiClient';
import { validateAgainst } from '../validation/validateAgainst';
import { TechAssessmentSchema } from '../validation/schemas';
import { GridRecord } from '../../components/Grid/GridDataTypes';
import { techAssessmentForGrid } from '../../../snrwb/components/TechAssessments/TechAssessments';
import momencik from '../../momencik';
import DictionaryStatic from '../../../snrwb/components/Dictionary/DictionaryStatic';
import { SnrwbCoreContextType } from '..';
import OrganizationalUnitBadge from '../../../snrwb/components/OrganizationalUnit/OrganizationalUnitBadge';
import * as AttachmentContext from '../../../common/snrwbCore/contexts/AttachmentContext';

export type TechAssessmentDto =
  | GetTechAssessmentDto
  | CreateUpdateTechAssessmentDto;

export interface TechAssessmentContextInterface {
  getById: (id: string) => Promise<GetTechAssessmentDto>;
  getAllByPortion: (
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
  ) => Promise<GridRecord[]>;
  getAllCount: (filterText?: string) => Promise<number>;
  getByOrganization: (id: string) => Promise<GetTechAssessmentDto[]>;
  create: (dto: CreateUpdateTechAssessmentDto) => Promise<GetTechAssessmentDto>;
  createWithOrganization: (
    techAssessment: CreateUpdateTechAssessmentDto,
    organization: CreateUpdateOrganizationDto,
  ) => Promise<GetTechAssessmentDto>;
  update: (id: string, dto: CreateUpdateTechAssessmentDto) => Promise<void>;
  delete: (id: string) => Promise<void>;
}

export const TechAssessmentContext = (api: DefaultApi) => {
  return {
    getById: (id: string) => api.techAssessmentControllerGet(id),
    getAllByPortion: async (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
    ) => {
      const data = await api.techAssessmentControllerGetAllByPortion(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
      );
      const items = [];
      for (const item of data) {
        const atts = await api.attachmentControllerGetAllForObject(
          AttachmentContext.ObjectType.TechAssessment,
          item.id,
        );
        items.push(techAssessmentForGrid(item, atts));
      }
      return items;
    },

    getAllCount: (filterText?: string) =>
      api.techAssessmentControllerGetAllCount(filterText || ''),

    getByOrganization: (id: string) =>
      api
        .techAssessmentControllerGetByOrganization(id)
        .then(cert =>
          cert.sort((a, b) => a.number?.localeCompare(b.number, 'pl')),
        ),
    create: (techAssessment: CreateUpdateTechAssessmentDto) =>
      api.techAssessmentControllerCreate(techAssessment),
    createWithOrganization: (
      techAssessment: CreateUpdateTechAssessmentDto,
      organization: CreateUpdateOrganizationDto,
    ) =>
      api.techAssessmentControllerCreateWithOrganization({
        techAssessment: techAssessment,
        organization: organization,
      }),
    update: (id: string, techAssessment: CreateUpdateTechAssessmentDto) =>
      api.techAssessmentControllerUpdate(id, techAssessment),
    delete: (id: string) => api.techAssessmentControllerDelete(id),
  };
};

export const validate = (techAssessment: CreateUpdateTechAssessmentDto) =>
  validateAgainst(TechAssessmentSchema, techAssessment);

export const newTechAssessment = () => {
  return CreateUpdateTechAssessmentDtoFromJSON({});
};

export const convert = (techAssessment: GetTechAssessmentDto) => {
  if (!techAssessment) {
    return newTechAssessment();
  }
  const converted = CreateUpdateTechAssessmentDtoFromJSON(techAssessment);
  converted.organizationalUnitId = techAssessment.organizationalUnit?.id;
  converted.producerId = techAssessment.producer?.id;
  converted.groupProductTypeId = techAssessment.groupProductType?.id;

  return converted;
};

export const forPresentation = (techAssessment: GetTechAssessmentDto) => {
  const form = [];
  if (techAssessment.producer) {
    form.push({
      name: 'Wnioskodawca',
      value: techAssessment.producer.name,
    });
  }
  if (techAssessment.organizationalUnit) {
    form.push({
      name: 'Jednostka oceny technicznej',
      value: OrganizationalUnitBadge({
        organizationalUnit: techAssessment.organizationalUnit,
      }),
    });
  }
  if (techAssessment.preparationDate) {
    form.push({
      name: 'Data wydania oceny',
      value: momencik(techAssessment.preparationDate),
    });
  }
  if (techAssessment.number) {
    form.push({
      name: 'Numer krajowej oceny technicznej',
      value: techAssessment.number,
    });
  }
  if (techAssessment.productName) {
    form.push({
      name: 'Nazwa wyrobu budowlanego',
      value: techAssessment.productName,
    });
  }
  if (techAssessment.productType) {
    form.push({
      name: 'Rodzaj wyrobu budowlanego',
      value: techAssessment.productType,
    });
  }
  if (techAssessment.productPurpose) {
    form.push({
      name: 'Zamierzone zastosowanie wyrobu budowlanego',
      value: techAssessment.productPurpose,
    });
  }
  if (techAssessment.groupProductType) {
    form.push({
      name: 'Grupa wyrobów',
      value: DictionaryStatic({ value: techAssessment.groupProductType }),
    });
  }
  if (techAssessment.groupProductTypeOther) {
    form.push({
      name: 'Inna grupa wyrobu',
      value: techAssessment.groupProductTypeOther,
    });
  }
  if (techAssessment.expDateStart) {
    form.push({
      name: 'Okres ważności Krajowej Oceny Technicznej od',
      value: momencik(techAssessment.expDateStart),
    });
  }
  if (techAssessment.expDateEnd) {
    form.push({
      name: 'Okres ważności Krajowej Oceny Technicznej do',
      value: momencik(techAssessment.expDateEnd),
    });
  }
  if (techAssessment.repealDate) {
    form.push({
      name: 'Data  uchylenia krajowej oceny technicznej',
      value: momencik(techAssessment.repealDate),
    });
  }
  if (techAssessment.note) {
    form.push({
      name: 'Uwagi',
      value: techAssessment.note,
    });
  }

  return form;
};

const addDictionaryHistoryEntry = async (
  snrwb: SnrwbCoreContextType,
  name: string,
  dictionaryId: string,
  prevDictionaryId: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: any[],
) => {
  const dict = DictionaryStatic({
    value: await snrwb.dictionaries.getById(dictionaryId),
  });

  let prevDict = undefined;

  if (prevDictionaryId) {
    prevDict = DictionaryStatic({
      value: await snrwb.dictionaries.getById(prevDictionaryId),
    });
  }
  form.push({
    name: name,
    value: dict,
    prevValue: prevDict,
    equal: dictionaryId === prevDictionaryId,
  });
};

const addAttachmentsHistoryEntry = async (
  snrwb: SnrwbCoreContextType,
  id: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: any[],
) => {
  const atts = await snrwb.attachments.getAllForObject(
    AttachmentContext.ObjectType.TechAssessment,
    id,
  );

  form.push({
    name: 'Załączniki',
    value: atts.map(item => item.name).join(', '),
    equal: true,
  });
};

export const forPresentationHistory = async (
  snrwb: SnrwbCoreContextType,
  techAssessment: CreateUpdateTechAssessmentDto & { id: string },
  prevTechAssessment: CreateUpdateTechAssessmentDto,
) => {
  const form = [];

  form.push({
    name: 'Jednostka oceny technicznej',
    value: OrganizationalUnitBadge({
      organizationalUnit: await snrwb.organizationalUnits.getById(
        techAssessment.organizationalUnitId,
      ),
    }),
    prevValue: prevTechAssessment
      ? OrganizationalUnitBadge({
          organizationalUnit: await snrwb.organizationalUnits.getById(
            prevTechAssessment.organizationalUnitId,
          ),
        })
      : undefined,
    equal:
      techAssessment.organizationalUnitId ===
      prevTechAssessment?.organizationalUnitId,
  });

  if (techAssessment.preparationDate) {
    form.push({
      name: 'Data wydania oceny',
      value: momencik(techAssessment.preparationDate),
      prevValue: momencik(prevTechAssessment?.preparationDate),
      equal:
        techAssessment.preparationDate === prevTechAssessment?.preparationDate,
    });
  }

  form.push({
    name: 'Numer krajowej oceny technicznej',
    value: techAssessment.number,
    prevValue: prevTechAssessment?.number,
    equal: techAssessment.number === prevTechAssessment?.number,
  });

  form.push({
    name: 'Nazwa wyrobu budowlanego',
    value: techAssessment.productName,
    prevValue: prevTechAssessment?.productName,
    equal: techAssessment.productName === prevTechAssessment?.productName,
  });

  form.push({
    name: 'Rodzaj wyrobu budowlanego',
    value: techAssessment.productType,
    prevValue: prevTechAssessment?.productType,
    equal: techAssessment.productType === prevTechAssessment?.productType,
  });

  form.push({
    name: 'Zamierzone zastosowanie wyrobu budowlanego',
    value: techAssessment.productPurpose,
    prevValue: prevTechAssessment?.productPurpose,
    equal: techAssessment.productPurpose === prevTechAssessment?.productPurpose,
  });

  await addDictionaryHistoryEntry(
    snrwb,
    'Grupa wyrobów',
    techAssessment?.groupProductTypeId,
    prevTechAssessment?.groupProductTypeId,
    form,
  );

  if (techAssessment.groupProductTypeOther) {
    form.push({
      name: 'Inna grupa wyrobu',
      value: techAssessment.groupProductTypeOther,
      prevValue: prevTechAssessment?.groupProductTypeOther,
      equal:
        techAssessment.groupProductTypeOther ===
        prevTechAssessment?.groupProductTypeOther,
    });
  }

  if (techAssessment.expDateStart) {
    form.push({
      name: 'Okres ważności Krajowej Oceny Technicznej od',
      value: momencik(techAssessment.expDateStart),
      prevValue: momencik(prevTechAssessment?.expDateStart),
      equal: techAssessment.expDateStart === prevTechAssessment?.expDateStart,
    });
  }

  if (techAssessment.expDateEnd) {
    form.push({
      name: 'Okres ważności Krajowej Oceny Technicznej do',
      value: momencik(techAssessment.expDateEnd),
      prevValue: momencik(prevTechAssessment?.expDateEnd),
      equal: techAssessment.expDateEnd === prevTechAssessment?.expDateEnd,
    });
  }

  if (techAssessment.repealDate) {
    form.push({
      name: 'Data  uchylenia krajowej oceny technicznej',
      value: momencik(techAssessment.repealDate),
      prevValue: momencik(prevTechAssessment?.repealDate),
      equal: techAssessment.repealDate === prevTechAssessment?.repealDate,
    });
  }

  form.push({
    name: 'Uwagi',
    value: techAssessment.note,
    prevValue: prevTechAssessment?.note,
    equal: techAssessment.note === prevTechAssessment?.note,
  });

  await addAttachmentsHistoryEntry(snrwb, techAssessment.id, form);

  return form;
};
